import { Trash2 } from "lucide-react";
import {
  CreateBase,
  EditBase,
  Form,
  useDelete,
  useEditContext,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState } from "react";
import { Confirm } from "@/components/admin/confirm";
import { SaveButton } from "@/components/admin/form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { CrmEvent } from "../types";
import { EventCalendarStatus } from "./EventCalendarStatus";
import { EventInputs } from "./EventInputs";
import { useCalendarSync } from "./useCalendarSync";

const DIALOG_CLASS_NAME =
  "lg:max-w-2xl overflow-y-auto max-h-9/10 top-1/20 translate-y-0";

/** Creates an event prefilled with `record` (company, contact, start date...). */
export const EventCreateDialog = ({
  open,
  onClose,
  record,
  title,
}: {
  open: boolean;
  onClose: () => void;
  record: Partial<CrmEvent>;
  title?: string;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const syncCalendar = useCalendarSync();
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className={DIALOG_CLASS_NAME}>
        {/* Mounted with the dialog content, so each opening starts from `record`. */}
        <CreateBase
          resource="events"
          record={record}
          redirect={false}
          mutationOptions={{
            onSuccess: (event: CrmEvent) => {
              notify("resources.events.added");
              onClose();
              syncCalendar({ event_id: event.id });
            },
          }}
        >
          <Form className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>
                {title ?? translate("resources.events.dialog.create")}
              </DialogTitle>
            </DialogHeader>
            <EventInputs />
            <DialogFooter>
              <SaveButton />
            </DialogFooter>
          </Form>
        </CreateBase>
      </DialogContent>
    </Dialog>
  );
};

/** Edits (or deletes) the event `eventId`; closed when it is null. */
export const EventEditDialog = ({
  eventId,
  onClose,
}: {
  eventId: Identifier | null;
  onClose: () => void;
}) => {
  const notify = useNotify();
  const syncCalendar = useCalendarSync();
  return (
    <Dialog
      open={eventId != null}
      onOpenChange={(isOpen) => !isOpen && onClose()}
    >
      <DialogContent className={DIALOG_CLASS_NAME}>
        {eventId != null && (
          <EditBase
            resource="events"
            id={eventId}
            redirect={false}
            mutationMode="pessimistic"
            mutationOptions={{
              onSuccess: (event: CrmEvent) => {
                notify("resources.events.updated");
                onClose();
                syncCalendar({ event_id: event.id });
              },
            }}
          >
            <EventEditForm onDeleted={onClose} />
          </EditBase>
        )}
      </DialogContent>
    </Dialog>
  );
};

const EventEditForm = ({ onDeleted }: { onDeleted: () => void }) => {
  const translate = useTranslate();
  const { record, isPending } = useEditContext<CrmEvent>();
  if (isPending || !record) return null;
  return (
    <Form className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>{translate("resources.events.dialog.edit")}</DialogTitle>
      </DialogHeader>
      <EventInputs />
      <EventCalendarStatus event={record} />
      <DialogFooter className="sm:justify-between">
        <DeleteEventButton record={record} onDeleted={onDeleted} />
        <SaveButton />
      </DialogFooter>
    </Form>
  );
};

const DeleteEventButton = ({
  record,
  onDeleted,
}: {
  record: CrmEvent;
  onDeleted: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const syncCalendar = useCalendarSync();
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [deleteEvent, { isPending }] = useDelete<CrmEvent>(
    "events",
    { id: record.id, previousData: record },
    {
      mutationMode: "pessimistic",
      onSuccess: () => {
        setConfirmOpen(false);
        notify("resources.events.deleted");
        onDeleted();
        // Cancels the event in the calendars it reached.
        syncCalendar({ event_id: record.id });
      },
    },
  );
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        className="text-destructive hover:text-destructive cursor-pointer"
        onClick={() => setConfirmOpen(true)}
      >
        <Trash2 className="size-4" />
        {translate("resources.events.action.delete")}
      </Button>
      <Confirm
        isOpen={isConfirmOpen}
        title="resources.events.action.delete"
        content="ra.message.are_you_sure"
        loading={isPending}
        onConfirm={() => deleteEvent()}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
};
